import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import {
  listCompanyUsers,
  listWithholdings,
  createWithholdingApi,
  updateWithholdingApi,
  deleteWithholdingApi,
  cancelWithholdingApi,
  getWithholdingHistory,
  listRecurringWithholdings,
  createRecurringWithholdingApi,
  updateRecurringWithholdingApi,
  deleteRecurringWithholdingApi,
  stopRecurringWithholdingApi,
  getRecurringWithholdingHistory,
  exportWithholdingsRegister,
  listDocumentsApi,
  listLocations,
} from "../api";
import { ROLES } from "../constants";
import { todayParts } from "../utils/dates";
import {
  computeWithholdingAmount,
  enabledWithholdingKindDefs,
  kindLabel,
  kindLimitPct,
} from "../controllers/withholdings";
import LocationFilter, { applyLocationFilter } from "../components/LocationFilter";
import EmployeeNameFilter, { filterUsersByName } from "../components/EmployeeNameFilter";
import AppPage from "../components/AppPage";

const MONTH_KEYS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const fmt = (n) => Number(n || 0).toLocaleString("ru-RU", { maximumFractionDigits: 2 });

export default function Withholdings() {
  const { t } = useTranslation();
  const { user, company } = useAuth();
  const tp = todayParts();
  const [tab, setTab] = useState("monthly");
  const [year, setYear] = useState(tp.year);
  const [month, setMonth] = useState(tp.month);
  const [users, setUsers] = useState([]);
  const [rows, setRows] = useState([]);
  const [recurringRows, setRecurringRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationFilter, setLocationFilter] = useState("all");
  const [nameFilter, setNameFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [commentTarget, setCommentTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [exporting, setExporting] = useState(false);

  const kindDefs = useMemo(() => enabledWithholdingKindDefs(company), [company]);

  const isAdmin = user && [ROLES.DIRECTOR, ROLES.HR].includes(user.role);
  const isManager = user?.role === ROLES.MANAGER;
  const canCreate = isAdmin || isManager;

  const allowedUsers = useMemo(() => {
    if (!user) return [];
    let base;
    if (isAdmin) base = users;
    else if (isManager) base = users.filter((u) => u.user_id === user.user_id || u.manager_id === user.user_id);
    else base = users.filter((u) => u.user_id === user.user_id);
    return applyLocationFilter(base, locationFilter);
  }, [users, user, isAdmin, isManager, locationFilter]);

  const visibleUsers = useMemo(
    () => filterUsersByName(allowedUsers, nameFilter),
    [allowedUsers, nameFilter],
  );

  const reload = async () => {
    setLoading(true);
    const [u, wh, rwh, locs] = await Promise.all([
      listCompanyUsers(),
      listWithholdings({ year, month, status: "all" }).catch(() => []),
      listRecurringWithholdings({ status: "all" }).catch(() => []),
      listLocations().catch(() => []),
    ]);
    setUsers(u || []);
    setRows(wh || []);
    setRecurringRows(rwh || []);
    setLocations(locs || []);
    setLoading(false);
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [year, month]);

  const byUser = useMemo(() => {
    const m = new Map();
    for (const w of rows) {
      if (!m.has(w.user_id)) m.set(w.user_id, []);
      m.get(w.user_id).push(w);
    }
    return m;
  }, [rows]);

  const recurringByUser = useMemo(() => {
    const m = new Map();
    for (const w of recurringRows) {
      if (!m.has(w.user_id)) m.set(w.user_id, []);
      m.get(w.user_id).push(w);
    }
    return m;
  }, [recurringRows]);

  const onDelete = async (id) => {
    if (!window.confirm(t("withholdings.confirm_delete"))) return;
    try {
      await deleteWithholdingApi(id);
      await reload();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
  };

  const onDeleteRecurring = async (id) => {
    if (!window.confirm(t("withholdings.confirm_delete_recurring"))) return;
    try {
      await deleteRecurringWithholdingApi(id);
      await reload();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
  };

  const onSave = async (form) => {
    try {
      if (editing) {
        await updateWithholdingApi(editing.withholding_id, form);
      } else {
        await createWithholdingApi(form);
      }
      setShowModal(false);
      setEditing(null);
      await reload();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
  };

  const onSaveRecurring = async (form) => {
    try {
      if (editing) {
        await updateRecurringWithholdingApi(editing.recurring_withholding_id, form);
      } else {
        await createRecurringWithholdingApi(form);
      }
      setShowRecurringModal(false);
      setEditing(null);
      await reload();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
  };

  const onCancelWithComment = async (comment) => {
    if (!commentTarget) return;
    try {
      if (commentTarget.type === "monthly") {
        await cancelWithholdingApi(commentTarget.id, comment);
      } else {
        await stopRecurringWithholdingApi(commentTarget.id, comment);
      }
      setCommentTarget(null);
      await reload();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
  };

  const onExport = async () => {
    setExporting(true);
    try {
      await exportWithholdingsRegister(year, month);
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <AppPage testId="withholdings-page" bodyScroll="scroll">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h4 className="fw-bold mb-0">
          <i className="bi bi-dash-circle text-danger me-2"></i>
          {t("withholdings.title")}
        </h4>
        <div className="d-flex gap-2 flex-wrap align-items-center">
          {(isAdmin || isManager) && (
            <EmployeeNameFilter value={nameFilter} onChange={setNameFilter} testid="withholdings-name-filter" />
          )}
          {(isAdmin || isManager) && (
            <LocationFilter locations={locations} users={isAdmin ? users : []}
                            value={locationFilter} onChange={setLocationFilter}
                            testid="withholdings-location-filter" />
          )}
          {tab === "monthly" && isAdmin && (
            <button type="button" className="btn btn-outline-success btn-sm"
                    onClick={onExport} disabled={exporting} data-testid="withholdings-export">
              <i className="bi bi-file-earmark-excel me-1"></i>
              {exporting ? t("common.loading") : t("withholdings.export_register")}
            </button>
          )}
          {tab === "monthly" && (
            <>
              <select className="form-select form-select-sm" style={{ width: 150 }} value={month}
                      onChange={(e) => setMonth(Number(e.target.value))} data-testid="withholdings-month">
                {MONTH_KEYS.map((mk, i) => <option key={mk} value={i}>{t(`months.${mk}`)}</option>)}
              </select>
              <select className="form-select form-select-sm" style={{ width: 100 }} value={year}
                      onChange={(e) => setYear(Number(e.target.value))} data-testid="withholdings-year">
                {[year - 1, year, year + 1].map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </>
          )}
        </div>
      </div>

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button type="button" className={`nav-link ${tab === "monthly" ? "active" : ""}`}
                  onClick={() => setTab("monthly")}>{t("withholdings.tab_monthly")}</button>
        </li>
        <li className="nav-item">
          <button type="button" className={`nav-link ${tab === "recurring" ? "active" : ""}`}
                  onClick={() => setTab("recurring")}>{t("withholdings.tab_recurring")}</button>
        </li>
      </ul>

      <p className="text-muted small mb-3">
        {tab === "monthly" ? t("withholdings.subtitle") : t("withholdings.subtitle_recurring")}
      </p>

      {canCreate && (
        <button type="button" className="btn btn-primary btn-sm mb-3"
                data-testid={tab === "monthly" ? "withholdings-add" : "withholdings-add-recurring"}
                onClick={() => {
                  setEditing(null);
                  if (tab === "monthly") setShowModal(true);
                  else setShowRecurringModal(true);
                }}>
          <i className="bi bi-plus-circle me-1"></i>
          {tab === "monthly" ? t("withholdings.add") : t("withholdings.add_recurring")}
        </button>
      )}

      {loading ? (
        <div className="text-center py-5 text-muted">
          <span className="spinner-border spinner-border-sm me-2"></span>{t("common.loading")}
        </div>
      ) : allowedUsers.length === 0 ? (
        <div className="alert alert-light border">{t("withholdings.empty")}</div>
      ) : visibleUsers.length === 0 ? (
        <div className="alert alert-light border">{t("common.search_employee_empty")}</div>
      ) : tab === "monthly" ? (
        visibleUsers.map((u) => {
          const items = byUser.get(u.user_id) || [];
          if (!canCreate && items.length === 0) return null;
          const total = items.reduce((s, w) => {
            if (w.status === "cancelled") return s;
            return s + computeWithholdingAmount(w, u.salary, 0);
          }, 0);
          return (
            <UserMonthlyCard key={u.user_id} user={u} items={items} total={total} canCreate={canCreate}
                             company={company} kindDefs={kindDefs}
                             onEdit={(w) => { setEditing(w); setShowModal(true); }}
                             onDelete={onDelete}
                             onCancel={(w) => setCommentTarget({ type: "monthly", id: w.withholding_id, name: w.name })}
                             onHistory={(w) => setHistoryTarget({ type: "monthly", id: w.withholding_id, name: w.name })}
                             t={t} />
          );
        })
      ) : (
        visibleUsers.map((u) => {
          const items = recurringByUser.get(u.user_id) || [];
          if (!canCreate && items.length === 0) return null;
          return (
            <UserRecurringCard key={u.user_id} user={u} items={items} canCreate={canCreate}
                              company={company} kindDefs={kindDefs}
                              onEdit={(w) => { setEditing(w); setShowRecurringModal(true); }}
                              onDelete={onDeleteRecurring}
                              onStop={(w) => setCommentTarget({ type: "recurring", id: w.recurring_withholding_id, name: w.name })}
                              onHistory={(w) => setHistoryTarget({ type: "recurring", id: w.recurring_withholding_id, name: w.name })}
                              t={t} />
          );
        })
      )}

      {showModal && (
        <WithholdingModal
          editing={editing}
          users={allowedUsers}
          year={year}
          month={month}
          kindDefs={kindDefs}
          company={company}
          onClose={() => { setShowModal(false); setEditing(null); }}
          onSave={onSave}
        />
      )}
      {showRecurringModal && (
        <RecurringWithholdingModal
          editing={editing}
          users={allowedUsers}
          kindDefs={kindDefs}
          company={company}
          onClose={() => { setShowRecurringModal(false); setEditing(null); }}
          onSave={onSaveRecurring}
        />
      )}
      {commentTarget && (
        <CommentModal
          title={commentTarget.type === "monthly" ? t("withholdings.cancel_title") : t("withholdings.stop_title")}
          subtitle={commentTarget.name}
          onClose={() => setCommentTarget(null)}
          onSubmit={onCancelWithComment}
          t={t}
        />
      )}
      {historyTarget && (
        <HistoryModal
          target={historyTarget}
          onClose={() => setHistoryTarget(null)}
          t={t}
        />
      )}
    </AppPage>
  );
}

function UserMonthlyCard({ user, items, total, canCreate, company, kindDefs, onEdit, onDelete, onCancel, onHistory, t }) {
  return (
    <div className="card card-soft mb-3" data-testid={`withholdings-user-${user.user_id}`}>
      <div className="card-header bg-light d-flex justify-content-between align-items-center">
        <strong>{user.name}</strong>
        {total > 0 && <span className="text-danger fw-semibold">−{fmt(total)} ₸</span>}
      </div>
      {items.length === 0 ? (
        <div className="card-body small text-muted">{t("withholdings.user_empty")}</div>
      ) : (
        <WithholdingTable user={user} items={items} canCreate={canCreate} company={company} kindDefs={kindDefs}
                          onEdit={onEdit} onDelete={onDelete} onCancel={onCancel} onHistory={onHistory} t={t} />
      )}
    </div>
  );
}

function UserRecurringCard({ user, items, canCreate, company, kindDefs, onEdit, onDelete, onStop, onHistory, t }) {
  return (
    <div className="card card-soft mb-3" data-testid={`recurring-withholdings-user-${user.user_id}`}>
      <div className="card-header bg-light"><strong>{user.name}</strong></div>
      {items.length === 0 ? (
        <div className="card-body small text-muted">{t("withholdings.recurring_empty")}</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm mb-0 align-middle">
            <thead className="table-light">
              <tr>
                <th>{t("withholdings.f_kind")}</th>
                <th>{t("withholdings.f_name")}</th>
                <th className="text-end">{t("withholdings.f_amount")}</th>
                <th>{t("withholdings.f_period")}</th>
                <th>{t("withholdings.f_status")}</th>
                {canCreate && <th></th>}
              </tr>
            </thead>
            <tbody>
              {items.map((w) => (
                <tr key={w.recurring_withholding_id} className={w.status === "stopped" ? "text-muted" : ""}>
                  <td>
                    <span className="badge bg-danger-subtle text-danger">
                      {kindLabel(w.kind, company, t)}
                    </span>
                  </td>
                  <td>
                    {w.name}
                    {w.remaining_installments != null && (
                      <div className="small text-muted">{t("withholdings.installments_left", { count: w.remaining_installments })}</div>
                    )}
                  </td>
                  <td className="text-end text-danger fw-semibold">
                    −{fmt(computeWithholdingAmount(w, user.salary, 0))} ₸
                    {w.amount_mode === "percent_salary" && <div className="small text-muted">{w.amount}%</div>}
                  </td>
                  <td className="small">{w.valid_from} — {w.valid_to || "∞"}</td>
                  <td>
                    <span className={`badge ${w.status === "active" ? "bg-success-subtle text-success" : "bg-secondary-subtle text-secondary"}`}>
                      {t(`withholdings.status_${w.status}`)}
                    </span>
                  </td>
                  {canCreate && (
                    <td className="text-end text-nowrap">
                      <button type="button" className="btn btn-sm btn-link" title={t("withholdings.history")}
                              onClick={() => onHistory(w)}>
                        <i className="bi bi-clock-history"></i>
                      </button>
                      {w.status === "active" && (
                        <button type="button" className="btn btn-sm btn-link text-warning" title={t("withholdings.stop")}
                                onClick={() => onStop(w)}>
                          <i className="bi bi-pause-circle"></i>
                        </button>
                      )}
                      <button type="button" className="btn btn-sm btn-link" onClick={() => onEdit(w)}>
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button type="button" className="btn btn-sm btn-link text-danger"
                              onClick={() => onDelete(w.recurring_withholding_id)}>
                        <i className="bi bi-trash"></i>
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function WithholdingTable({ user, items, canCreate, company, kindDefs, onEdit, onDelete, onCancel, onHistory, t }) {
  return (
    <div className="table-responsive">
      <table className="table table-sm mb-0 align-middle">
        <thead className="table-light">
          <tr>
            <th>{t("withholdings.f_kind")}</th>
            <th>{t("withholdings.f_name")}</th>
            <th className="text-end">{t("withholdings.f_amount")}</th>
            <th>{t("withholdings.f_timing")}</th>
            <th>{t("withholdings.f_basis")}</th>
            {canCreate && <th></th>}
          </tr>
        </thead>
        <tbody>
          {items.map((w) => (
            <tr key={w.withholding_id} className={w.status === "cancelled" ? "text-muted text-decoration-line-through" : ""}>
              <td>
                <span className="badge bg-danger-subtle text-danger">
                  {kindLabel(w.kind, company, t)}
                </span>
                {w.status === "cancelled" && (
                  <span className="badge bg-secondary-subtle text-secondary ms-1">{t("withholdings.status_cancelled")}</span>
                )}
              </td>
              <td>{w.name}</td>
              <td className="text-end text-danger fw-semibold">
                −{fmt(computeWithholdingAmount(w, user.salary, 0))} ₸
                {w.amount_mode === "percent_salary" && <div className="small text-muted">{w.amount}%</div>}
              </td>
              <td className="small">{t(`withholdings.timing_${w.tax_timing || "post_tax"}`)}</td>
              <td className="small text-muted">
                {w.basis || w.reason || "—"}
                {w.document_id && <div className="text-primary">{t("withholdings.linked_document")}</div>}
              </td>
              {canCreate && (
                <td className="text-end text-nowrap">
                  <button type="button" className="btn btn-sm btn-link" title={t("withholdings.history")}
                          onClick={() => onHistory(w)}>
                    <i className="bi bi-clock-history"></i>
                  </button>
                  {w.status !== "cancelled" && (
                    <button type="button" className="btn btn-sm btn-link text-warning" title={t("withholdings.cancel")}
                            onClick={() => onCancel(w)}>
                      <i className="bi bi-x-circle"></i>
                    </button>
                  )}
                  <button type="button" className="btn btn-sm btn-link" onClick={() => onEdit(w)}>
                    <i className="bi bi-pencil"></i>
                  </button>
                  <button type="button" className="btn btn-sm btn-link text-danger" onClick={() => onDelete(w.withholding_id)}>
                    <i className="bi bi-trash"></i>
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WithholdingModal({ editing, users, year, month, kindDefs, company, onClose, onSave }) {
  const { t } = useTranslation();
  const [selectedIds, setSelectedIds] = useState(editing ? [editing.user_id] : (users[0] ? [users[0].user_id] : []));
  const [kind, setKind] = useState(editing?.kind || "penalty");
  const [name, setName] = useState(editing?.name || "");
  const [amountMode, setAmountMode] = useState(editing?.amount_mode || "fixed");
  const [amount, setAmount] = useState(editing?.amount ?? "");
  const [taxTiming, setTaxTiming] = useState(editing?.tax_timing || "post_tax");
  const [basis, setBasis] = useState(editing?.basis || "");
  const [documentRef, setDocumentRef] = useState(editing?.document_ref || "");
  const [documentId, setDocumentId] = useState(editing?.document_id || "");
  const [employeeConsent, setEmployeeConsent] = useState(!!editing?.employee_consent);
  const [consentDate, setConsentDate] = useState(editing?.consent_date || "");
  const [reason, setReason] = useState(editing?.reason || "");
  const [saving, setSaving] = useState(false);

  const toggleUser = (id) => {
    if (editing) return;
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return alert(t("withholdings.err_name"));
    if (!selectedIds.length) return alert(t("withholdings.err_users"));
    setSaving(true);
    try {
      if (editing) {
        await onSave({
          kind, name: name.trim(), amount_mode: amountMode, amount: Number(amount) || 0,
          tax_timing: taxTiming, basis, document_ref: documentRef, document_id: documentId || null,
          employee_consent: employeeConsent, consent_date: consentDate || null, reason,
        });
      } else {
        await onSave({
          user_ids: selectedIds, year, month,
          kind, name: name.trim(), amount_mode: amountMode, amount: Number(amount) || 0,
          tax_timing: taxTiming, basis, document_ref: documentRef, document_id: documentId || null,
          employee_consent: employeeConsent, consent_date: consentDate || null, reason,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell title={editing ? t("withholdings.edit") : t("withholdings.add")} onClose={onClose}
                onSubmit={submit} saving={saving} testid="withholdings-modal">
      {!editing && users.length > 0 && (
        <UserPicker
          users={users}
          selectedIds={selectedIds}
          onToggle={toggleUser}
          onSelectAll={(ids) => setSelectedIds(ids)}
          onClear={() => setSelectedIds([])}
          t={t}
        />
      )}
      <WithholdingFields kind={kind} setKind={setKind} name={name} setName={setName}
                         amountMode={amountMode} setAmountMode={setAmountMode} amount={amount} setAmount={setAmount}
                         taxTiming={taxTiming} setTaxTiming={setTaxTiming} basis={basis} setBasis={setBasis}
                         documentRef={documentRef} setDocumentRef={setDocumentRef}
                         documentId={documentId} setDocumentId={setDocumentId}
                         userId={selectedIds[0]} kindDefs={kindDefs} company={company}
                         employeeConsent={employeeConsent} setEmployeeConsent={setEmployeeConsent}
                         consentDate={consentDate} setConsentDate={setConsentDate}
                         reason={reason} setReason={setReason} t={t} />
    </ModalShell>
  );
}

function RecurringWithholdingModal({ editing, users, kindDefs, company, onClose, onSave }) {
  const { t } = useTranslation();
  const [selectedIds, setSelectedIds] = useState(editing ? [editing.user_id] : (users[0] ? [users[0].user_id] : []));
  const [kind, setKind] = useState(editing?.kind || "loan");
  const [name, setName] = useState(editing?.name || "");
  const [amountMode, setAmountMode] = useState(editing?.amount_mode || "fixed");
  const [amount, setAmount] = useState(editing?.amount ?? "");
  const [taxTiming, setTaxTiming] = useState(editing?.tax_timing || "post_tax");
  const [validFrom, setValidFrom] = useState(editing?.valid_from || new Date().toISOString().slice(0, 10));
  const [validTo, setValidTo] = useState(editing?.valid_to || "");
  const [installments, setInstallments] = useState(editing?.remaining_installments ?? "");
  const [status, setStatus] = useState(editing?.status || "active");
  const [basis, setBasis] = useState(editing?.basis || "");
  const [documentRef, setDocumentRef] = useState(editing?.document_ref || "");
  const [documentId, setDocumentId] = useState(editing?.document_id || "");
  const [employeeConsent, setEmployeeConsent] = useState(!!editing?.employee_consent);
  const [consentDate, setConsentDate] = useState(editing?.consent_date || "");
  const [reason, setReason] = useState(editing?.reason || "");
  const [saving, setSaving] = useState(false);

  const toggleUser = (id) => {
    if (editing) return;
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return alert(t("withholdings.err_name"));
    if (!selectedIds.length) return alert(t("withholdings.err_users"));
    if (!validFrom) return alert(t("withholdings.err_valid_from"));
    setSaving(true);
    try {
      const base = {
        kind, name: name.trim(), amount_mode: amountMode, amount: Number(amount) || 0,
        tax_timing: taxTiming, valid_from: validFrom, valid_to: validTo || null,
        remaining_installments: installments === "" ? null : Number(installments),
        basis, document_ref: documentRef, document_id: documentId || null,
        employee_consent: employeeConsent, consent_date: consentDate || null, reason,
      };
      if (editing) {
        await onSave({ ...base, status });
      } else {
        await onSave({ ...base, user_ids: selectedIds });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell title={editing ? t("withholdings.edit_recurring") : t("withholdings.add_recurring")}
                onClose={onClose} onSubmit={submit} saving={saving} testid="recurring-withholdings-modal">
      {!editing && users.length > 0 && (
        <UserPicker
          users={users}
          selectedIds={selectedIds}
          onToggle={toggleUser}
          onSelectAll={(ids) => setSelectedIds(ids)}
          onClear={() => setSelectedIds([])}
          t={t}
        />
      )}
      {editing && (
        <div className="mb-3">
          <label className="form-label">{t("withholdings.f_status")}</label>
          <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="active">{t("withholdings.status_active")}</option>
            <option value="stopped">{t("withholdings.status_stopped")}</option>
          </select>
        </div>
      )}
      <div className="row g-3 mb-3">
        <div className="col-md-6">
          <label className="form-label">{t("withholdings.f_valid_from")} *</label>
          <input type="date" className="form-control" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} required />
        </div>
        <div className="col-md-6">
          <label className="form-label">{t("withholdings.f_valid_to")}</label>
          <input type="date" className="form-control" value={validTo || ""} onChange={(e) => setValidTo(e.target.value)} />
          <div className="form-text">{t("withholdings.valid_to_hint")}</div>
        </div>
        <div className="col-md-6">
          <label className="form-label">{t("withholdings.f_installments")}</label>
          <input type="number" min="1" className="form-control" value={installments}
                 onChange={(e) => setInstallments(e.target.value)} placeholder="—" />
        </div>
      </div>
      <WithholdingFields kind={kind} setKind={setKind} name={name} setName={setName}
                         amountMode={amountMode} setAmountMode={setAmountMode} amount={amount} setAmount={setAmount}
                         taxTiming={taxTiming} setTaxTiming={setTaxTiming} basis={basis} setBasis={setBasis}
                         documentRef={documentRef} setDocumentRef={setDocumentRef}
                         documentId={documentId} setDocumentId={setDocumentId}
                         userId={selectedIds[0]} kindDefs={kindDefs} company={company}
                         employeeConsent={employeeConsent} setEmployeeConsent={setEmployeeConsent}
                         consentDate={consentDate} setConsentDate={setConsentDate}
                         reason={reason} setReason={setReason} t={t} />
    </ModalShell>
  );
}

function UserPicker({ users, selectedIds, onToggle, onSelectAll, onClear, t }) {
  const [search, setSearch] = useState("");
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q));
  }, [users, search]);

  return (
    <div className="mb-3">
      <label className="form-label fw-semibold">{t("withholdings.f_employees")}</label>
      <div className="d-flex gap-2 mb-2 flex-wrap align-items-center">
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onSelectAll(filtered.map((u) => u.user_id))}>
          {t("common.select_all")}
        </button>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClear}>
          {t("common.clear_selection")}
        </button>
        <span className="text-muted small">{t("common.selected_count", { n: selectedIds.length })}</span>
      </div>
      <input
        className="form-control form-control-sm mb-2"
        placeholder={t("common.search_employee_name")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        data-testid="withholdings-user-search"
      />
      <div style={{ maxHeight: 180, overflowY: "auto" }} className="border rounded p-2">
        {filtered.length === 0 ? (
          <div className="text-muted small py-2">{t("common.search_employee_empty")}</div>
        ) : filtered.map((u) => (
          <div className="form-check" key={u.user_id}>
            <input
              className="form-check-input"
              type="checkbox"
              id={`wh-u-${u.user_id}`}
              checked={selectedIds.includes(u.user_id)}
              onChange={() => onToggle(u.user_id)}
              data-testid={`withholding-user-check-${u.user_id}`}
            />
            <label className="form-check-label" htmlFor={`wh-u-${u.user_id}`}>
              {u.name}{" "}
              <span className="text-muted small">
                ({t(`roles.${u.role}`)} · {Number(u.salary || 0).toLocaleString("ru-RU")} ₸)
              </span>
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}

function WithholdingFields({
  kind, setKind, name, setName, amountMode, setAmountMode, amount, setAmount,
  taxTiming, setTaxTiming, basis, setBasis, documentRef, setDocumentRef,
  documentId, setDocumentId, userId, kindDefs, company,
  employeeConsent, setEmployeeConsent, consentDate, setConsentDate, reason, setReason, t,
}) {
  const [docs, setDocs] = useState([]);
  const kinds = kindDefs?.length ? kindDefs : [];
  const limitPct = kindLimitPct(company, kind);

  useEffect(() => {
    if (!userId) { setDocs([]); return; }
    listDocumentsApi({ user_id: userId }).then((list) => setDocs(list || [])).catch(() => setDocs([]));
  }, [userId]);

  return (
    <div className="row g-3">
      <div className="col-md-6">
        <label className="form-label">{t("withholdings.f_kind")}</label>
        <select className="form-select" value={kind} onChange={(e) => setKind(e.target.value)}>
          {kinds.map((k) => <option key={k.key} value={k.key}>{k.name}</option>)}
        </select>
      </div>
      <div className="col-md-6">
        <label className="form-label">{t("withholdings.f_timing")}</label>
        <select className="form-select" value={taxTiming} onChange={(e) => setTaxTiming(e.target.value)}>
          <option value="post_tax">{t("withholdings.timing_post_tax")}</option>
          <option value="pre_tax">{t("withholdings.timing_pre_tax")}</option>
        </select>
        <div className="form-text">{t("withholdings.timing_hint")}</div>
      </div>
      <div className="col-12">
        <label className="form-label">{t("withholdings.f_name")} *</label>
        <input className="form-control" value={name} onChange={(e) => setName(e.target.value)}
               placeholder={t("withholdings.name_placeholder")} required />
      </div>
      <div className="col-md-4">
        <label className="form-label">{t("withholdings.f_amount_mode")}</label>
        <select className="form-select" value={amountMode} onChange={(e) => setAmountMode(e.target.value)}>
          <option value="fixed">{t("withholdings.mode_fixed")}</option>
          <option value="percent_salary">{t("withholdings.mode_percent_salary")}</option>
        </select>
      </div>
      <div className="col-md-4">
        <label className="form-label">{amountMode === "fixed" ? `${t("withholdings.f_amount")} (₸)` : `${t("withholdings.f_amount")} (%)`}</label>
        <input type="number" className="form-control" min="0" step="any" value={amount}
               onChange={(e) => setAmount(e.target.value)} required />
      </div>
      <div className="col-md-4">
        <label className="form-label">{t("withholdings.f_document_ref")}</label>
        <input className="form-control" value={documentRef} onChange={(e) => setDocumentRef(e.target.value)}
               placeholder={t("withholdings.document_placeholder")} />
      </div>
      <div className="col-12">
        <label className="form-label">{t("withholdings.f_document_file")}</label>
        <select className="form-select" value={documentId || ""} onChange={(e) => setDocumentId(e.target.value || "")}>
          <option value="">{t("withholdings.no_document")}</option>
          {docs.map((d) => (
            <option key={d.doc_id} value={d.doc_id}>{d.title} ({d.category || "—"})</option>
          ))}
        </select>
        <div className="form-text">{t("withholdings.document_file_hint")}</div>
      </div>
      <div className="col-12">
        <label className="form-label">{t("withholdings.f_basis")} *</label>
        <input className="form-control" value={basis} onChange={(e) => setBasis(e.target.value)}
               placeholder={t("withholdings.basis_placeholder")} required />
      </div>
      <div className="col-12">
        <label className="form-label">{t("withholdings.f_reason")}</label>
        <textarea className="form-control" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
      </div>
      <div className="col-md-6">
        <div className="form-check">
          <input className="form-check-input" type="checkbox" id="wh-consent" checked={employeeConsent}
                 onChange={(e) => setEmployeeConsent(e.target.checked)} />
          <label className="form-check-label" htmlFor="wh-consent">{t("withholdings.f_consent")}</label>
        </div>
      </div>
      <div className="col-md-6">
        <label className="form-label">{t("withholdings.f_consent_date")}</label>
        <input type="date" className="form-control" value={consentDate}
               onChange={(e) => setConsentDate(e.target.value)} disabled={!employeeConsent} />
      </div>
      <div className="col-12">
        <div className="alert alert-warning small mb-0">
          <i className="bi bi-exclamation-triangle me-1"></i>
          {t("withholdings.limit_dynamic", { pct: limitPct })}
        </div>
      </div>
    </div>
  );
}

function CommentModal({ title, subtitle, onClose, onSubmit, t }) {
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return alert(t("withholdings.err_comment"));
    setSaving(true);
    try {
      await onSubmit(comment.trim());
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.4)" }} data-testid="withholding-comment-modal">
      <div className="modal-dialog">
        <form className="modal-content" onSubmit={submit}>
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            {subtitle && <p className="text-muted small">{subtitle}</p>}
            <label className="form-label">{t("withholdings.f_comment")} *</label>
            <textarea className="form-control" rows={3} value={comment}
                      onChange={(e) => setComment(e.target.value)} required
                      placeholder={t("withholdings.comment_placeholder")} />
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>{t("common.cancel")}</button>
            <button type="submit" className="btn btn-warning" disabled={saving}>
              {saving ? t("common.loading") : t("common.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function HistoryModal({ target, onClose, t }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = target.type === "monthly"
      ? getWithholdingHistory(target.id)
      : getRecurringWithholdingHistory(target.id);
    load.then((list) => setEvents(list || [])).catch(() => setEvents([])).finally(() => setLoading(false));
  }, [target]);

  const actionLabel = (action) => t(`withholdings.event_${action}`, action);

  return (
    <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.4)" }} data-testid="withholding-history-modal">
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{t("withholdings.history_title")}: {target.name}</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            {loading ? (
              <div className="text-center text-muted py-3">{t("common.loading")}</div>
            ) : events.length === 0 ? (
              <div className="text-muted">{t("withholdings.history_empty")}</div>
            ) : (
              <ul className="list-group list-group-flush">
                {events.map((ev) => (
                  <li key={ev.event_id} className="list-group-item px-0">
                    <div className="d-flex justify-content-between">
                      <strong>{actionLabel(ev.action)}</strong>
                      <span className="small text-muted">{ev.created_at?.slice(0, 16).replace("T", " ")}</span>
                    </div>
                    <div className="small text-muted">{ev.actor_name || ev.actor_id}</div>
                    {ev.comment && <div className="small mt-1">{ev.comment}</div>}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>{t("common.close")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ModalShell({ title, children, onClose, onSubmit, saving, testid }) {
  const { t } = useTranslation();
  return (
    <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.4)" }} data-testid={testid}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <form className="modal-content" onSubmit={onSubmit}>
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">{children}</div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>{t("common.cancel")}</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? t("common.loading") : t("common.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
